import React from 'react';
import { CheckCircle2Icon, GitMergeIcon } from 'lucide-react';
import { WallCard } from './WallCard';
import { SlideToConfirm } from '../../ui/SlideToConfirm';
import { useDb } from '../../../hooks/useDb';
import { proposalDetail } from '../../../utils/dbSelectors';
import { euro } from '../../../utils/time';
import type { Proposal } from '../../../types/db';
interface ProposalFocusCardProps {
  proposal: Proposal;
  position: number;
  total: number;
  compact: boolean;
  onApprove: () => void;
  onReject: () => void;
  onEdit: () => void;
  onLater: () => void;
}
const SLIDE: Record<Proposal['kind'], {
  label: string;
  done: string;
}> = {
  mail: {
    label: 'Schuif om te versturen',
    done: 'Verstuurd'
  },
  code: {
    label: 'Schuif om goed te keuren',
    done: 'Goedgekeurd · mergen doe je zelf'
  },
  boekhouding: {
    label: 'Schuif om in te boeken',
    done: 'Ingeboekt'
  }
};
const btn = 'h-14 whitespace-nowrap rounded-full px-6 text-[22px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60';

/** Niveau 3, one at a time and large. Only slide-to-confirm is visible; the rest sits behind the bottom line. */
export function ProposalFocusCard({
  proposal,
  position,
  total,
  compact,
  onApprove,
  onReject,
  onEdit,
  onLater
}: ProposalFocusCardProps) {
  const detail = useDb(t => proposalDetail(t, proposal), [proposal]);
  const slide = SLIDE[proposal.kind];
  return <WallCard size="focus" agent={proposal.agent} kicker={`voorstel ${position} van ${total}`} title={proposal.title} level="naOk" where={proposal.where} traceId={proposal.traceId} actions={<>
          <button type="button" onClick={onEdit} className={`${btn} border border-white/10 text-ink-soft hover:border-white/25 hover:text-ink`} data-id="element-1352" data-mp="99bag2tz0i8z">
            Aanpassen
          </button>
          {total > 1 && <button type="button" onClick={onLater} className={`${btn} border border-white/10 text-ink-soft hover:border-white/25 hover:text-ink`} data-id="element-1353" data-mp="2b299whi9692w">
              Later
            </button>}
          <button type="button" onClick={onReject} className={`${btn} text-ink-muted hover:bg-white/[0.05] hover:text-ink`} data-id="element-1354" data-mp="j0prgc4k3mxs">
            Afwijzen
          </button>
        </>} data-id="element-1351" data-mp="48x4cqbxjidl">
      {detail.kind === 'mail' && <>
          <p className="text-ink-muted" data-id="element-1355" data-mp="svrgz92qur0w">
            Aan {detail.to} · {detail.subject}
          </p>
          <p className={`mt-2 text-ink ${compact ? 'line-clamp-2' : ''}`} data-id="element-1356" data-mp="wxd21hpfmbm6">“{detail.body}”</p>
        </>}

      {detail.kind === 'code' && <>
          <p className="text-ink" data-id="element-1357" data-mp="16qagncrazgfi">{detail.pr.title}</p>
          {!compact && <ul className="mt-3 space-y-1 font-mono text-[22px]" data-id="element-1358" data-mp="1bolnrfq7rcu1">
              {detail.pr.files.map(f => <li key={f.path} className="flex justify-between gap-4" data-id="element-1359" data-mp="1gogd3eucbucz">
                  <span className="truncate" data-id="element-1360" data-mp="1koihvwvdh3wh">{f.path}</span>
                  <span className="shrink-0 tabular-nums" data-id="element-1361" data-mp="1kmxmk518hgxk">
                    <span className="text-[#8FD99A]" data-id="element-1362" data-mp="35842vjqr5rmn">+{f.additions}</span> <span className="text-[#F29B8F]" data-id="element-1363" data-mp="354q2ui5k9uid">−{f.deletions}</span>
                  </span>
                </li>)}
            </ul>}
          <p className="mt-3 flex items-center gap-2.5 text-[#8FD99A]" data-id="element-1364" data-mp="2w760t6zvju6p">
            <CheckCircle2Icon className="h-6 w-6 shrink-0" aria-hidden data-id="element-1365" data-mp="2r833k8q4ys9a" />
            {detail.pr.tests.passed}/{detail.pr.tests.passed + detail.pr.tests.failed} tests geslaagd
          </p>
          <p className="mt-1 flex items-center gap-2.5 text-ink" data-id="element-1366" data-mp="3jvfetmqujdna">
            <GitMergeIcon className="h-6 w-6 shrink-0 text-ink-muted" aria-hidden data-id="element-1367" data-mp="3jybcd9ys80b7" /> Mergen doe je zelf
          </p>
        </>}

      {detail.kind === 'boekhouding' && <>
          <ul className="space-y-1" data-id="element-1368" data-mp="3a41l5vlkt822">
            {detail.receipts.map(r => <li key={r.id} className="flex justify-between gap-4" data-id="element-1369" data-mp="bhq989su01g0">
                <span className="truncate text-ink" data-id="element-1370" data-mp="6p5hrexee0fn">{r.vendor}</span>
                <span className="shrink-0 tabular-nums text-ink" data-id="element-1371" data-mp="3nctf6hegueho">{euro(r.amount)}</span>
              </li>)}
          </ul>
          <p className="mt-3 tabular-nums" data-id="element-1372" data-mp="3rfgaisacihjm">
            Totaal {euro(detail.total)} · btw {euro(detail.vat)} · gekoppeld aan de bank
          </p>
        </>}

      <div className="mt-6" data-id="element-1373" data-mp="3idp8bxf0ksui">
        <SlideToConfirm size="wall" label={slide.label} confirmedLabel={slide.done} onConfirm={onApprove} data-id="element-1374" data-mp="3icxcqmlf2v48" />
      </div>
    </WallCard>;
}